// A step-up (ADR-0019) as the api serves it: a code sent for one sensitive action, then that code or the person's
// PIN, just before the action. The api keeps the step-up on the session and spends it on the action; the web app
// keeps nothing.
import { ApiError, call, type Fetch } from "../http.js";

/** The actions the api asks a step-up for (packages/access `SENSITIVE_ACTIONS`). */
export const SENSITIVE_ACTIONS = [
  "bank_details.change",
  "roles.change",
  "owner.add",
  "export.full",
  "payment.approve",
  "pin.change",
  "tenant.offboard",
] as const;
export type SensitiveAction = (typeof SENSITIVE_ACTIONS)[number];

const isSensitiveAction = (value: string): value is SensitiveAction =>
  (SENSITIVE_ACTIONS as readonly string[]).includes(value);

/** A step-up a refusal asks for: the action, and whether the api lets the person's PIN confirm it. */
export interface StepUpAsked {
  readonly action: SensitiveAction;
  readonly pinAllowed: boolean;
}

/**
 * The step-up a refusal asks for, or undefined when it is not asking for one. Whether a PIN will do is the api's
 * word (`pin_allowed`), never a copy kept here; when it says nothing, only a code is offered.
 */
export function stepUpFor(error: unknown): StepUpAsked | undefined {
  if (!(error instanceof ApiError) || error.code !== "step_up_required") return undefined;
  if (error.action === undefined || !isSensitiveAction(error.action)) return undefined;
  return { action: error.action, pinAllowed: error.pinAllowed === true };
}

export interface StepUpApi {
  /** Sends the signed-in person a code for `action`: to their Telegram, else their email. */
  requestCode(action: SensitiveAction): Promise<void>;
  /** Steps the session up for `action` with the code sent for it, or the person's PIN. */
  confirm(action: SensitiveAction, proof: { readonly code: string } | { readonly pin: string }): Promise<void>;
}

export function createStepUpApi(fetcher: Fetch): StepUpApi {
  return {
    async requestCode(action) {
      await call(fetcher, "/auth/step-up/code", { json: { action } });
    },
    async confirm(action, proof) {
      await call(fetcher, "/auth/step-up", { json: { action, ...proof } });
    },
  };
}
